import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock } from 'lucide-react';
import { Card } from '@caruso/ui';
import { eventsService } from '@/services/events.service';

export function PublicEventPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: () => eventsService.getBySlug(slug!),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <div className="text-white text-xl">Carregando evento...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-brand-ink flex items-center justify-center">
        <Card className="p-8 bg-brand-smoke border-brand-gold/20 max-w-md">
          <h1 className="text-2xl font-bold text-white mb-4">Evento não encontrado</h1>
          <p className="text-brand-ivory/70">
            Este evento não está disponível ou não foi publicado ainda.
          </p>
        </Card>
      </div>
    );
  }

  const eventDate = new Date(event.eventDate);
  const formattedDate = eventDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const formattedTime = eventDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="min-h-screen bg-brand-ink">
      {/* Banner */}
      {event.bannerUrl && (
        <div className="w-full h-64 md:h-96 relative">
          <img
            src={event.bannerUrl}
            alt={event.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-brand-dark" />
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {event.name}
            </h1>
            {event.description && (
              <p className="text-xl text-brand-ivory/80 max-w-2xl mx-auto">
                {event.description}
              </p>
            )}
          </div>

          {/* Event Details Card */}
          <Card className="p-8 bg-brand-smoke border-brand-gold/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date */}
              <div className="flex items-start gap-4">
                <div className="p-3 bg-brand-gold/10 rounded-lg">
                  <Calendar className="w-6 h-6 text-brand-gold" />
                </div>
                <div>
                  <p className="text-brand-ivory/60 text-sm mb-1">Data</p>
                  <p className="text-white font-semibold text-lg">{formattedDate}</p>
                </div>
              </div>

              {/* Time */}
              <div className="flex items-start gap-4">
                <div className="p-3 bg-brand-gold/10 rounded-lg">
                  <Clock className="w-6 h-6 text-brand-gold" />
                </div>
                <div>
                  <p className="text-brand-ivory/60 text-sm mb-1">Horário</p>
                  <p className="text-white font-semibold text-lg">{formattedTime}</p>
                </div>
              </div>

              {/* Location */}
              {event.location && (
                <div className="flex items-start gap-4 md:col-span-2">
                  <div className="p-3 bg-brand-gold/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-brand-gold" />
                  </div>
                  <div>
                    <p className="text-brand-ivory/60 text-sm mb-1">Local</p>
                    <p className="text-white font-semibold text-lg">{event.location}</p>
                  </div>
                </div>
              )}

              {/* Guest Count */}
              <div className="flex items-start gap-4 md:col-span-2">
                <div className="p-3 bg-brand-gold/10 rounded-lg">
                  <Users className="w-6 h-6 text-brand-gold" />
                </div>
                <div>
                  <p className="text-brand-ivory/60 text-sm mb-1">Convidados</p>
                  <p className="text-white font-semibold text-lg">
                    {event.guestCount} {event.guestCount === 1 ? 'pessoa convidada' : 'pessoas convidadas'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="p-6 bg-brand-gold/5 border-brand-gold/20">
            <p className="text-brand-ivory/80 text-center">
              Se você recebeu um convite com link de confirmação, clique no link para confirmar sua presença.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
